import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Download, Loader2 } from "lucide-react";

const CHUNK = 750 * 1024;
const TYPE_LABELS = { application: "Application", certification: "Certification", i9: "I-9", other: "Other" };
const TYPE_COLORS = {
  application: "bg-[#1E73C9]/10 text-[#1E73C9]",
  certification: "bg-[#166534]/10 text-[#166534]",
  i9: "bg-[#7C3AED]/10 text-[#7C3AED]",
  other: "bg-[#15233B]/10 text-[#15233B]/70",
};

function fmtSize(b) {
  if (b > 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b > 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
}

export default function DocumentsSection({ card, isArchived, onActivity, reloadKey }) {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("application");
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get(`/cards/${card.id}/documents`).then(({ data }) => setDocs(data)).catch(() => {});
  useEffect(() => { load(); }, [card.id, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadFile = async (file) => {
    setProgress(1);
    try {
      const { data: s } = await api.post("/uploads/start", {
        card_id: card.id, filename: file.name, doc_type: docType, total_size: file.size,
      });
      let index = 0;
      for (let off = 0; off < file.size; off += CHUNK) {
        const fd = new FormData();
        fd.append("index", index);
        fd.append("chunk", file.slice(off, off + CHUNK));
        await api.post(`/uploads/${s.upload_id}/chunk`, fd);
        index += 1;
        setProgress(Math.min(95, Math.round(((off + CHUNK) / file.size) * 100)));
      }
      await api.post(`/uploads/${s.upload_id}/complete`);
      toast.success(`${file.name} uploaded`);
      load();
      onActivity?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document deleted");
      load();
      onActivity?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="documents-section">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">Documents</div>
      {!isArchived && (
        <div className="flex items-center gap-2 mb-2">
          <select
            data-testid="doc-type-select"
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="border border-[#15233B]/15 rounded-md px-2 py-1.5 text-xs bg-white"
          >
            <option value="application">Application</option>
            <option value="certification">Certification</option>
            <option value="i9">I-9</option>
            <option value="other">Other</option>
          </select>
          <button
            data-testid="doc-upload-button"
            disabled={progress !== null}
            onClick={() => fileRef.current?.click()}
            className="px-3 py-1.5 rounded-md bg-[#15233B] hover:bg-[#1E73C9] text-white text-xs font-extrabold flex items-center gap-1 disabled:opacity-60"
          >
            {progress !== null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {progress !== null ? `${progress}%` : "Upload"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx"
            className="hidden"
            data-testid="doc-file-input"
            onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
          />
          <span className="text-[10px] text-[#15233B]/45">PDF, images or Word · max 25 MB</span>
        </div>
      )}
      {docs.length === 0 ? (
        <div className="text-xs italic text-[#15233B]/40 bg-white border border-[#15233B]/10 rounded-md px-3 py-3">
          No documents yet — upload the application, certifications or I-9.
        </div>
      ) : (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-2 bg-white border border-[#15233B]/10 rounded-md px-3 py-2">
              <FileText className="h-4 w-4 text-[#15233B]/40 shrink-0" />
              <button
                onClick={() => download(d)}
                data-testid={`doc-download-${d.id}`}
                title="Download"
                className="text-xs font-bold text-[#15233B] hover:text-[#1E73C9] truncate text-left flex-1 min-w-0"
              >
                {d.filename}
              </button>
              <span className={`text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded ${TYPE_COLORS[d.doc_type] || TYPE_COLORS.other}`}>
                {TYPE_LABELS[d.doc_type] || d.doc_type}
              </span>
              <span className="text-[10px] text-[#15233B]/45 shrink-0">{fmtSize(d.size)}</span>
              <button onClick={() => download(d)} className="text-[#15233B]/40 hover:text-[#1E73C9]" title="Download">
                <Download className="h-3.5 w-3.5" />
              </button>
              {!isArchived && (
                <button onClick={() => remove(d)} data-testid={`doc-delete-${d.id}`} className="text-[#15233B]/40 hover:text-[#b91c1c]" title="Delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
